import React, { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { Box, DialogContentText, Divider, Grid, Stack, TextField } from "@mui/material"

import { useGlobalLoader, useLogger, UserContext } from "src/providers"

import { RequestError } from "src/models/errors"
import {
  createMultilangJob,
  SimpleExecutorRecord,
  Comment,
  stripCategoryPrefix,
  ReferenceFile,
  getJobName
} from "src/services/api/document"
import UserService from "src/UserService"
import { FilesItem } from "src/sites/file/droparea/FileDropzone"
import { postJobCreated, postTabletVisited } from "src/services/api/metrics"
import { deleteFile, langMissmatch, uploadFiles, UploadedJobFile } from "./droparea/expiringFiles"
import TwoOptionsDialog from "./TwoOptionsDialog"
import UsageLimitDialog from "./UsageLimitDialog"
import PageContainer from "src/components/page/PageContainer"
import {
  GENRES,
  Genre,
  stageSort,
  tabletAllowsSameSourceAndTarget,
  tabletRequiresSameSourceAndTarget
} from "../../models/common"
import CustomAutocomplete from "src/components/CustomAutocomplete"
import { Community } from "@models/user"
import { ColoredBox, ColoredGrid } from "./ColoredGridItem"
import { InstructionsDialog } from "src/components/InstructionsDialog"
import { ExecutorsSelection } from "./ExecutorsSelection"
import { LanguagesSelection } from "./LanguagesSelection"
import { TranslateFileDropzone } from "./TranslateFileDropzone"
import { dateWithTime } from "src/utils/dateHelper"
import { useLocalStorage } from "src/utils/useLocalStorage"
import { useTranslateFile } from "src/sites/file/useTranslateFile"
import { useSnackbar } from "src/providers/SnackbarProvider"
import { useResponsive } from "src/utils/useResponsive"
import { appBarHeight } from "src/providers/AppProviders"
import { Language } from "src/utils/useLanguageList"
import { ingestFilesToKnowledgeBases } from "src/utils/kmIngest"

const TranslateFilePage = () => {
  const { snackbarShowMessage } = useSnackbar()
  const { isMobile } = useResponsive()

  const currentUser = () => {
    const { id, name, surname, login } = UserService.getTokenUserInfo()
    return { id: id, firstName: name, lastName: surname, userName: login }
  }

  const { userCommunities } = useContext(UserContext)
  const { showLoader, hideLoader } = useGlobalLoader()
  const {
    tablet,
    withInsourcingTrusminer,
    withInsourcingEnhancer,
    showInstructionsButton,
    workflowWithAnonymizatioin,
    isOutsourcing,
    allowedLanguages,
    taxonomies,
    langFrom,
    setLangFrom,
    langsTo,
    setLangsTo,
    genre,
    setGenre
  } = useTranslateFile()
  const quoting = tablet.quoting
  const [files, setFiles] = useState<FilesItem[]>([])
  const [dialogSummary, setDialogSummary] = useState<boolean>(false)
  const [instructions, setInstructions] = useState<boolean>(false)
  const [languageMissmatch, setLanguageMismatch] = useState<boolean>(false)
  const [enhancer, setEnhancer] = useState<SimpleExecutorRecord>(currentUser())
  const [trustminer, setTrustminer] = useState<SimpleExecutorRecord | undefined>(undefined)
  const [communities, setCommunities] = useLocalStorage<Community[]>(
    "initialCommunities",
    userCommunities || [],
    (value: Community[]) => value.every((v) => userCommunities?.some((c) => c.gatedCommunityId === v.gatedCommunityId))
  )
  const [referenceFiles, setReferenceFiles] = useState<ReferenceFile[]>([])
  const [comments, setComments] = useState<Comment[]>([])
  const [projectId, setProjectId] = useState<string>()

  const { t } = useTranslation("common")
  const navigate = useNavigate()

  const { logger } = useLogger()

  const noValidFiles = files.filter((f) => f.uploadedFile && f.uploadedFile.typedValidationError === null).length <= 0
  const noCommunity = communities.length < 1
  const noTargetLanguages = langsTo.length < 1
  const noSourceLanguage = !langFrom

  useEffect(() => {
    postTabletVisited(tablet.name || tablet.id)
    clear()
  }, [tablet])

  const clear = () => {
    setFiles([])
    setReferenceFiles([])
    setComments([])
  }

  const allowSameSourceAndTarget = tabletAllowsSameSourceAndTarget(tablet.stages)
  const requireSameSourceAndTarget = tabletRequiresSameSourceAndTarget(tablet.stages)

  useEffect(() => {
    if (!requireSameSourceAndTarget) return
    setLangsTo([langFrom])
  }, [requireSameSourceAndTarget, langFrom, setLangsTo])

  const validateTargetLanguages = (languagesTo: Language[]): Language[] | null => {
    if (requireSameSourceAndTarget) {
      if (languagesTo.length !== 1 || languagesTo[0].languageId !== langFrom.languageId) {
        snackbarShowMessage("Target language must match source language for this workflow.", "error")
        return null
      }
      return languagesTo
    }

    const filteredLanguagesTo = allowSameSourceAndTarget
      ? languagesTo
      : languagesTo.filter((lang) => lang.languageId !== langFrom.languageId)

    if (filteredLanguagesTo.length === 0) {
      snackbarShowMessage("Target language cannot be the same as source language. Job was not created.", "error")
      return null
    }

    return filteredLanguagesTo
  }

  const checkLanguageMismatch = (ignoreLanguageMismatch: boolean): boolean => {
    if (ignoreLanguageMismatch) return false

    return files.some((f) => langMissmatch(langFrom.languageId, f.uploadedFile && f.uploadedFile.detectedLanguages))
  }

  const prepareStages = () => {
    return tablet.stages
      .map((s) => {
        if (s.stage === "TRUST_MINING" && s.executorSelection === "insourcing" && trustminer) {
          return { ...s, preselectedExecutorId: trustminer.id }
        }
        if (s.stage === "ENHANCEMENT" && s.executorSelection === "insourcing") {
          return { ...s, preselectedExecutorId: enhancer.id }
        }
        return s
      })
      .sort((s1, s2) => stageSort(s1.stage, s2.stage))
  }

  const sendDocument = async (ignoreLanguageMismatch: boolean) => {
    try {
      const languagesTo = tablet.multiLang ? langsTo : [langsTo[0]]
      const filteredLanguagesTo = validateTargetLanguages(languagesTo)

      if (!filteredLanguagesTo) return
      if (checkLanguageMismatch(ignoreLanguageMismatch)) {
        setLanguageMismatch(true)
        return
      }

      showLoader()

      await createMultilangJob({
        inputFilesWithName: files
          .map((f) => f.uploadedFile)
          .filter((f): f is NonNullable<typeof f> => f !== undefined)
          .map((f) => ({ fileId: f.id, name: f.name })),
        referenceFiles: referenceFiles.map((f) => f.id),
        type: "COMMERCIAL",
        genre: genre.id,
        langFrom: langFrom.languageId,
        langsTo: filteredLanguagesTo.map((l) => l.languageId),
        taxonomy: taxonomies,
        visibility: {
          open: false,
          communities: communities.map((c) => c.gatedCommunityId)
        },
        stages: prepareStages(),
        externalProjectId: projectId,
        requesterNote: comments?.[0]?.content,
        name: getJobName(files.map((f) => f.uploadedFile).filter((f) => f) as UploadedJobFile[]),
        pricingType: tablet.pricingType,
        quoting: quoting,
        tabletName: tablet.title,
        generateTcert: tablet.generateTcert,
        pricingRuleId: tablet.pricingRuleId
      })

      // Ingest files to knowledge bases
      const uploadFiles = files
        .map((file) => file.toUploadFile?.file)
        .filter((file): file is File => file !== undefined)

      const destinations = communities.map((community) => ({
        id: community.gatedCommunityId,
        name: community.gatedCommunityName
      }))

      if (uploadFiles.length > 0 && destinations.length > 0) {
        await ingestFilesToKnowledgeBases(uploadFiles, destinations, true)
      }

      setDialogSummary(true)
      setFiles([])
      postJobCreated(langFrom.languageId, langsTo.map((l) => l.languageId).join(","), tablet.name || tablet.id)
    } catch (e) {
      const dialogMessage =
        e instanceof RequestError && e.status === 401 ? t("document.permissionError") : t("document.uploadError")
      snackbarShowMessage(dialogMessage, "error")
      logger.error("User - Failed to upload document", e)
    } finally {
      hideLoader()
    }
  }

  const anyFileIncorrect = files.filter((f) => f.uploadedFile && f.uploadedFile.typedValidationError).length > 0

  const stackSpacing = isOutsourcing ? 4 : 3
  const sendButtonTooltip = workflowWithAnonymizatioin ? t("document.sendWithAnonymizationDescription") : ""

  const dialogs = (
    <>
      <TwoOptionsDialog
        open={dialogSummary}
        color="primary"
        title={t("document.summary.thankYou")}
        content={
          quoting ? (
            <DialogContentText>{t("document.summary.notifyQuoted")}</DialogContentText>
          ) : (
            <>
              <DialogContentText>{t("document.summary.emailInfo")}</DialogContentText>
              <DialogContentText>{t("document.summary.myFilesInfo")}</DialogContentText>
            </>
          )
        }
        firstOptionText={t("document.summary.translateAnother")}
        secondOptionText={t("document.summary.goToMyFiles")}
        firstOptionAction={() => {
          setDialogSummary(false)
          clear()
        }}
        secondOptionAction={() => {
          setDialogSummary(false)
          navigate("/files")
        }}
      />

      <TwoOptionsDialog
        open={languageMissmatch}
        color="warning"
        title={"Verify source language"}
        content={
          <DialogContentText>
            {
              "One or more files seem not to match the selected source language. Verify they should be part of this project."
            }
          </DialogContentText>
        }
        firstOptionText={"Let me double check"}
        secondOptionText={"Translate anyway"}
        firstOptionAction={() => setLanguageMismatch(false)}
        secondOptionAction={() => {
          setLanguageMismatch(false)
          sendDocument(true)
        }}
      />

      <UsageLimitDialog pageName={tablet.name || tablet.id} />
    </>
  )

  const executorsSelection = withInsourcingEnhancer && (
    <ExecutorsSelection
      enhancer={enhancer}
      setEnhancer={setEnhancer}
      trustminer={trustminer}
      setTrustminer={setTrustminer}
      withTrusminer={withInsourcingTrusminer}
      communities={communities}
    />
  )

  const languagesSelection = (
    <LanguagesSelection
      langFrom={langFrom}
      setLangFrom={setLangFrom}
      langsTo={langsTo}
      setLangsTo={setLangsTo}
      useMultiTargetLangs={requireSameSourceAndTarget ? false : tablet.multiLang}
      stackSpacing={stackSpacing}
      allowedLanguages={allowedLanguages}
      allowSameSourceAndTarget={allowSameSourceAndTarget}
      requireSameSourceAndTarget={requireSameSourceAndTarget}
    />
  )

  const communitiesSelection = (
    <CustomAutocomplete
      multiple
      value={communities}
      options={userCommunities || []}
      getOptionLabel={(option) => option.gatedCommunityName}
      onChange={(e, val) => {
        val && setCommunities(val as Community[])
      }}
      label={t("document.community")}
      isOptionEqualToValue={(o1, o2) => o1.gatedCommunityId === o2.gatedCommunityId}
      disabled={isMobile && userCommunities?.length === 1}
    />
  )

  const gridcolor = isOutsourcing ? "midnightBlue" : "lightSkyBlue"

  const translateFileDropzone = (
    <TranslateFileDropzone
      files={files}
      setFiles={setFiles}
      langFrom={langFrom.languageId}
      isMobile={isMobile}
      showInstructionsButton={showInstructionsButton}
      setInstructions={setInstructions}
      referenceFiles={referenceFiles}
      comments={comments}
      uploadFiles={uploadFiles}
      deleteFile={deleteFile}
      anyFileIncorrect={anyFileIncorrect}
      noValidFiles={noValidFiles}
      noCommunity={noCommunity}
      noTargetLanguages={noTargetLanguages}
      noSourceLanguage={noSourceLanguage}
      quoting={quoting}
      sendButtonTooltip={sendButtonTooltip}
      onSend={() => sendDocument(false)}
    />
  )

  if (isMobile)
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: `calc(100dvh - ${appBarHeight})` }}>
        <ColoredBox
          gridcolor={gridcolor}
          sx={{ py: 4, px: 2, flexGrow: 0, display: "flex", flexDirection: "column", gap: 2 }}
        >
          {languagesSelection}
          {executorsSelection}
          {communitiesSelection}
        </ColoredBox>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2, py: 2, px: 1 }}>
          {translateFileDropzone}
        </Box>
        {dialogs}
      </Box>
    )

  return (
    <PageContainer>
      <Grid container sx={{ height: "100%", borderRadius: "inherit" }}>
        <ColoredGrid
          gridcolor={gridcolor}
          item
          xs={3}
          md={4}
          sx={{
            borderTopLeftRadius: "inherit",
            borderBottomLeftRadius: "inherit"
          }}
        >
          <Stack justifyContent="space-between" sx={{ height: "100%" }}>
            {languagesSelection}
            {executorsSelection}

            <Divider sx={{ borderColor: "#00D2FF80" }} />
            <Stack spacing={stackSpacing} sx={{ p: 4, pb: 6, height: "100%" }}>
              {isOutsourcing && (
                <TextField
                  name="projectId"
                  label={t("document.projectId")}
                  onChange={(e) => setProjectId(e.target.value)}
                />
              )}
              <TextField
                name="taxonomy"
                label={t("document.taxonomy")}
                defaultValue={stripCategoryPrefix(taxonomies[0])}
                disabled
              />
              <CustomAutocomplete
                value={genre}
                options={GENRES}
                getOptionLabel={(option) => option.genre}
                onChange={(e, val) => val && setGenre(val as Genre)}
                label={t("document.genre")}
                isOptionEqualToValue={(o1, o2) => o1.id === o2.id}
                disableClearable
              />
              {communitiesSelection}
            </Stack>
          </Stack>
        </ColoredGrid>
        <Grid
          item
          xs={9}
          md={8}
          sx={{
            backgroundColor: "#FFFFFF",
            p: 5
          }}
        >
          {translateFileDropzone}

          <InstructionsDialog
            open={instructions}
            initialComments={comments}
            initialReferenceFiles={referenceFiles}
            onSubmit={(referenceFiles, comment) => {
              setInstructions(false)
              setReferenceFiles(referenceFiles)
              comment && setComments([{ content: comment, date: dateWithTime(Date.now()) }])
            }}
            onDismiss={() => setInstructions(false)}
          />
        </Grid>
      </Grid>
      {dialogs}
    </PageContainer>
  )
}

export default TranslateFilePage
